
import api from "../../services/api";
import { LiabilitiesResponse, Liability } from "../../types";

interface FetchLiabilitiesParams {
  estateId: string;
  page?: number;
  limit?: number;
  status?: 'open' | 'closed';
}

export class LiabilityService {
  /**
   * Fetch paginated liabilities along with total values and comparison metrics.
   * @param estateId - UUID of the estate.
   * @param page - Current page number.
   * @param limit - Number of liabilities per page.
   * @returns A promise that resolves to LiabilitiesResponse.
   */
  static async fetchLiabilities({ estateId, page = 1, limit = 10, status }: FetchLiabilitiesParams): Promise<LiabilitiesResponse> {
    try {
      const response = await api.get<LiabilitiesResponse>(`/liabilities/${estateId}`, {
        params: { page, limit, status },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching liabilities:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch liabilities.');
    }
  }

  /**
   * Fetch a single liability by ID.
   * @param estateId - UUID of the estate.
   * @param liabilityId - ID of the liability.
   * @returns A promise that resolves to a Liability.
   */
  static async fetchLiabilityById(estateId: string, liabilityId: string): Promise<Liability> {
    try {
      const response = await api.get<Liability>(`/liabilities/${estateId}/${liabilityId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching liability:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch liability.');
    }
  }

  /**
   * Create a new liability.
   * @param estateId - UUID of the estate.
   * @param liabilityData - Data for the new liability.
   * @returns A promise that resolves to the created Liability.
   */
  static async createLiability(estateId: string, liabilityData: Partial<Liability>): Promise<Liability> {
    try {
      const response = await api.post<Liability>(`/liabilities/${estateId}`, liabilityData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating liability:', error);
      throw error;
    }
  }

  /**
   * Update an existing liability.
   * @param estateId - UUID of the estate.
   * @param liabilityId - ID of the liability to update.
   * @param updateData - Updated data for the liability.
   * @returns A promise that resolves to the updated Liability.
   */
  static async updateLiability(estateId: string, liabilityId: string, updateData: Partial<Liability>): Promise<Liability> {
    try {
      const response = await api.patch<Liability>(`/liabilities/${estateId}/${liabilityId}`, updateData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating liability:', error);
      throw new Error(error.response?.data?.message || 'Failed to update liability.');
    }
  }

  /**
   * Delete a liability.
   * @param estateId - UUID of the estate.
   * @param liabilityId - ID of the liability to delete.
   * @returns A promise that resolves when the liability is deleted.
   */
  static async deleteLiability(estateId: string, liabilityId: string): Promise<void> {
    try {
      await api.delete(`/liabilities/${estateId}/${liabilityId}`);
    } catch (error: any) {
      console.error('Error deleting liability:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete liability.');
    }
  }
}
